<template>
  <div v-if="metric_type == 'input_status'">
    <v-dialog
      v-model="showDialog"
      max-width="75%"
    >
      <videon-card :heading="heading" showClose="true" @close="showDialog = false">
        <v-card-text>
          <div
            v-for="key in inputStatusKeys" v-bind:key="key" 
            style="display: inline-block; vertical-align: top;"
          >
            <v-card
              v-if="inputStatus[key]"
              min-width="320px"
              class="ma-3"
              outlined
            >
              <v-card-title class="text-capitalize">{{ nameLabel(key) }}</v-card-title>
              <v-card-text>
                <recursive-label :content="inputStatus[key]" style="overflow: auto; max-height: 300px;" />
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        <v-card>
          <v-card-title><v-icon>list</v-icon> Status History</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="changes"
              no-data-text="No changes"
              :items-per-page="500"
              mobile-breakpoint="0"
              hide-default-footer
            >
              <template v-slot:item.timestamp="{ item }">
                <span class="text-no-wrap">
                  {{ item.timestamp | moment("YYYY/MM/DD hh:mm:ss a") }}
                </span>
              </template>
              <template v-slot:item.change_data="{ item }">
                <div class="text-left">
                  <v-alert outlined dense rounded class="state-chip ma-1">
                    <recursive-diff-content :diffContent="item.change_data" />
                  </v-alert>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </videon-card>
    </v-dialog>
    
    <v-row 
      v-if="!error && inputStatus"
      @click="showDialog = true"
      class="mb-1"
      no-gutters
    >
      <v-chip v-if="inputStatus.locked !== undefined" class="mr-1 mb-1" small outlined>
        <strong>Input Status</strong>:&nbsp;
        <span v-if="inputStatus.locked">Locked</span>
        <span v-else>Not Locked</span>
      </v-chip>
      
      <v-chip v-if="inputStatus.video" class="mr-1 mb-1" small outlined>
        <strong>{{ $helpers.getLabel(inputStatus.video.input_type) }}</strong>:&nbsp;
        <span v-if="inputStatus.video.width">
          {{ inputStatus.video.width }}x{{ inputStatus.video.height }}<span v-if="inputStatus.video.interlaced">i</span><span v-else>p</span>
          <span v-if="inputStatus.video.fieldrate">{{ (inputStatus.video.fieldrate.numerator / inputStatus.video.fieldrate.denominator).toFixed(2) }}</span>
          <span v-if="inputStatus.video.timing && inputStatus.video.timing.field_rate_hz">{{ (inputStatus.video.timing.field_rate_hz.numerator / inputStatus.video.timing.field_rate_hz.denominator).toFixed(2) }}</span>
        </span>
        <span v-else>None</span>
      </v-chip>
      
      <v-chip v-if="inputStatus.audio" class="mr-1 mb-1" small outlined>
        <strong>{{ $helpers.getLabel(inputStatus.audio.input_type) }}</strong>:
        {{ inputStatus.audio.sample_rate_hz }}hz 
        ({{ inputStatus.audio.channel_count || 8 }} channels)
      </v-chip>
      
      <v-chip v-if="inputStatus.sdi_rx" class="mr-1 mb-1" small outlined>
        <strong>SDI PLL</strong>:&nbsp;
        <span v-if="inputStatus.sdi_rx.pll_lock">Locked</span>
        <span v-else>Unlocked</span>
      </v-chip>
      <v-chip v-if="inputStatus.hdmi_rx" class="mr-1 mb-1" small outlined>
        <strong>HDMI PLL</strong>:&nbsp;
        <span v-if="inputStatus.hdmi_rx.pll_lock">Locked</span>
        <span v-else>Unlocked</span>
      </v-chip>
      
      <v-chip v-if="ccDataLastSeen" class="mr-1 mb-1" small outlined>
        <strong>CC Data Last Seen</strong>:&nbsp;
        {{ ccDataLastSeen }}
      </v-chip>
      <v-chip v-if="SCTE104DataLastSeen" class="mr-1 mb-1" small outlined>
        <strong>SCTE-104 Data Last Seen</strong>:&nbsp;
        {{ SCTE104DataLastSeen }}
      </v-chip>
    </v-row>
    
    <input-status-label v-else :input="target" />
    
  </div>
</template>

<script>
  import moment from 'moment'

  export default {
    name: 'RTMetrics',
    
    props: ['device_guid', 'metric_type', 'target'],
    
    data() {
      return {
        supportedCloudVersion: this.$helpers.parseVersion('cloud', 'v1.3.0'),
        supportedDaemonVersion: this.$helpers.parseVersion('device', 'v11.3.0'),
        
        error: false,
        errorText: '',
        
        subscription: false,
        
        data: false,
        
        inputStatusKeys: ['video', 'audio', 'data', 'sdi_rx', 'hdmi_rx'],
        inputStatus: false,
        
        deviceTimeScew: false,
        
        headers: [
          { text: 'Time', align: 'start', value: 'timestamp', sortable: false },
          { text: 'Change', align: 'start', value: 'change_data', sortable: false }
        ],
        
        changes: [],
        
        duration: 30,
        interval: 5,
        
        requestInterval: 15000,
        requestTimer: false,
        
        hasFocus: true,
        showDialog: false,
        
        destroyed: false,
      }
    },
    
    computed: {
      heading() {
        if (this.metric_type == 'input_status') {
          if (this.target && this.target.config.name) {
            return this.target.config.name + ' Extended Input Status'
          }
          return 'Extended Input Status'
        }
        return this.metric_type
      },
      rtMetricsTopic() {
        //dt/videon_cloud/device/*/rt_metrics/*
        return 'dt/videon_cloud/device/' + this.device_guid + '/rt_metrics/' + this.metric_type
      },
      pubsub() {
        return this.$Amplify.PubSub
      },
      device() {
        return this.$devices.getDevice(this.device_guid)
      },
      isSupported() {
        if (
          this.$helpers.versionCheck(this.supportedCloudVersion, this.device.cloud_version)
          && this.$helpers.versionCheck(this.supportedDaemonVersion, this.device.daemon_version)
        ) {
          return true
        }
        return false
      },
      
      ccDataLastSeen() {
        if (this.inputStatus && this.inputStatus.data && this.inputStatus.data.cc_last_seen_utc) {
          var adjustedTime = moment().add(this.deviceTimeScew)
          return moment(this.inputStatus.data.cc_last_seen_utc).from(adjustedTime)
        }
        
        return false
      },
      SCTE104DataLastSeen() {
        if (this.inputStatus && this.inputStatus.data && this.inputStatus.data.scte104_last_seen_utc) {
          var adjustedTime = moment().add(this.deviceTimeScew)
          return moment(this.inputStatus.data.scte104_last_seen_utc).from(adjustedTime)
        }
        
        return false
      }
    },
    
    mounted() {
      setTimeout(() => {
        this.setupMetrics()
      }, this.$helpers.getRefreshInterval(250, 1000))
    },
    
    beforeDestroy() {
      this.destroyed = true
      
      clearTimeout(this.requestTimer)
      
      if (this.subscription) {
        console.log('RTMetrics ' + this.device_guid + ' unsubscribe ' + this.rtMetricsTopic)
        this.subscription.unsubscribe()
      }
    },
    
    methods: {
      nameLabel(stateName) {
        if (typeof stateName === 'string') {
          return stateName.replace(/_/g, ' ')
        } else if (typeof stateName === 'number') {
          return stateName
        } else if (typeof stateName === 'object') {
          return 'Object'
        }
        return typeof stateName
      },
      
      setupMetrics() {
        // handle all the error logic here...
        if (!this.device.isOnline()) {
          this.error = true
          this.errorText = 'Feature unavailable when the device is offline.'
          
          setTimeout(() => {
            this.setupMetrics()
          }, 5000)
        } else if (!this.isSupported) {
          this.error = true
          this.errorText = 'Feature requires cloud agent version ' + this.supportedCloudVersion.original + '.'
          
          setTimeout(() => {
            this.setupMetrics()
          }, 5000)
        } else if (!this.device.hasFeature('services.cloud.commands_interface.send_realtime_metric.enabled')) {
          this.error = true
          this.errorText = 'Feature is not licensed.'
          
          setTimeout(() => {
            this.setupMetrics()
          }, 5000)
        } else {
          this.subscribeToTopic()
          
          setTimeout(() => {
            this.requestMetrics(true)
          }, 250)
        }
      },
      
      subscribeToTopic() {
        if (!this.device_guid) {
          return
        }
        
        if (!this.subscription) {
          console.log('RTMetrics ' + this.device_guid + ' subscribeToTopic ' + this.rtMetricsTopic)
          
          this.subscription = this.pubsub.subscribe(this.rtMetricsTopic).subscribe({
            next: (data) => {
              if (data.value) {
                this.data = data.value
                
                if (this.metric_type == 'input_status' && this.target) {
                  var input = this.data.input_status.find(x => x.in_channel_id == this.target.id)
                  if (input && input.status) {
                    if (this.inputStatus && input.status) {
                      var diff = this.$helpers.recursiveDiff(this.inputStatus, input.status)
                      if (diff.length > 0) {
                        this.changes.unshift({ 'timestamp': new moment(), 'change_data': diff })
                        // only keep 20 changes
                        if (this.changes.length > 20) {
                          this.changes.pop()
                        }
                      }
                    }
                    
                    this.inputStatus = input.status
                    
                    // store time scew in MS, so that we can report time based things with an offset from local time...
                    this.deviceTimeScew = moment(this.inputStatus.device_time_utc).diff(moment())
                  }
                }
                
                // reset error
                this.error = false
                this.errorText = ''
              }
            },
            error: (error) => {
              console.log('RTMetrics ' + this.device_guid + ' subscribeToTopic Error:', error)
              this.error = true
              this.errorText = this.$helpers.parseError(error)
            },
            complete: () => {
              console.log('RTMetrics ' + this.device_guid + ' subscribeToTopic complete')
            }
          })
        }
      },
      
      async requestMetrics(firstRun = false) {
        if (this.destroyed) {
          return
        }
        
        this.hasFocus = document.hasFocus()
        
        if (!firstRun && !this.data && this.hasFocus) {
          this.error = true
          this.errorText = 'Data not received within ' + (this.requestInterval / 1000) + ' seconds.'
        }
        
        if (firstRun || this.hasFocus) {
          // types input_status/ping
          var commandObject = {'command': 'send_realtime_metric', 'data': {'metric_type': this.metric_type, 'duration': this.duration, 'interval': this.interval}}
        
          try {
            let response = await this.axios.post('/devices/' + this.device_guid + '/commands', commandObject)
            console.log('RTMetrics ' + this.device_guid + ' requestMetrics command response', response)
          } catch (error) {
            console.log('RTMetrics ' + this.device_guid + ' requestMetrics command error', this.$helpers.parseError(error))
          }
        }
        
        this.requestTimer = setTimeout(() => {
          if (!this.destroyed) {
            this.requestMetrics()
          }
        }, this.requestInterval)
      },
      
    }
  }
</script>
<style lang="scss" scoped>
  .state-chip {
     font-size: 12px;
     line-height: 20px;
     padding: 3px 12px;
     min-height: 24px;
     align-items: center;
     display: inline-flex;
  }
  .theme--light.state-chip {
      border-color: rgba(0, 0, 0, 0.12) !important;
      color: rgba(0, 0, 0, 0.87) !important;
  }
  .theme--dark.state-chip {
    border-color: rgba(255, 255, 255, 0.12) !important;
    color: #FFFFFF !important;
  }
</style>